import { useEffect, useMemo, useCallback, useState, useRef } from "react";
import { Feature } from "../../../ducks/feature/types";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../store";
import { featureActions, featureSelectors } from "../../../ducks/feature";
import { appActions } from "../../../ducks/app";
import { FeatureCreateRequestBody } from "../../../core/api/requests/feature/create/types";
import { ToastType } from "../../atoms/Toast";
import { FeatureEditRequestBody } from "../../../core/api/requests/feature/edit/types";
import { useModalWithProtection } from "../../../hooks/useModalWithProtection";

// Utility function to parse API error responses
const parseApiError = (error: any): string => {
  if (!error) {
    return "An unexpected error occurred. Please try again.";
  }

  // Handle HTTP status codes first
  if (error.response?.status) {
    const status = error.response.status;
    if (status === 504) {
      return "Gateway timeout. The server took too long to respond. Please try again.";
    }
    if (status === 503) {
      return "Service unavailable. The server is temporarily unavailable. Please try again later.";
    }
    if (status >= 500) {
      return "Server error. Please try again later or contact support.";
    }
  }

  // Handle timeout errors (ECONNABORTED or timeout messages)
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return "Request timeout. The server took too long to respond. Please try again.";
  }

  // Handle different error response formats
  if (error.response?.data) {
    const data = error.response.data;

    // Handle validation errors (Django REST framework format)
    if (typeof data === 'object') {
      const errorMessages: string[] = [];

      // Extract error messages from nested objects
      const extractErrors = (obj: any) => {
        for (const [key, value] of Object.entries(obj)) {
          if (Array.isArray(value)) {
            // Handle array of error messages
            value.forEach((msg: string) => {
              errorMessages.push(msg);
            });
          } else if (typeof value === 'object' && value !== null) {
            // Recursively handle nested objects
            extractErrors(value);
          } else if (typeof value === 'string') {
            // Handle direct string error messages
            errorMessages.push(value);
          }
        }
      };

      extractErrors(data);

      if (errorMessages.length > 0) {
        return errorMessages.join('\n');
      }
    }

    // Handle direct string error messages
    if (typeof data === 'string') {
      return data;
    }

    // Handle error message field
    if (data.message) {
      return data.message;
    }

    // Handle error detail field
    if (data.detail) {
      return data.detail;
    }
  }

  // Handle network errors - but don't show misleading message for timeouts
  // Timeouts will be handled specially in the delete handler
  if (error.message && !error.message.includes('timeout') && error.code !== 'ECONNABORTED') {
    if (error.message.includes('Network Error')) {
      // Only show network error if it's not a timeout
      return "Network error. Please check your connection and try again.";
    }
    return error.message;
  }

  // Fallback error message
  return "An unexpected error occurred. Please try again.";
};

export const useFeaturePage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const features = useSelector(featureSelectors.features);
  const pendingDeleteIdRef = useRef<string | null>(null);

  const refetchFeatures = useCallback(() => {
    dispatch(featureActions.fetchFeatures.started({}));
  }, [dispatch]);

  // Single useEffect to fetch features on mount only
  useEffect(() => {
    dispatch(featureActions.fetchFeatures.started({}));
  }, [dispatch]);

  // Check if pending deletion was successful after refetch
  useEffect(() => {
    if (pendingDeleteIdRef.current && features) {
      const wasDeleted = !features.some(feature => feature.id === pendingDeleteIdRef.current);
      if (wasDeleted) {
        // Deletion was successful despite timeout
        pendingDeleteIdRef.current = null;
        // Don't show error - just silently succeed since deletion worked
      } else {
        // Deletion failed - item still exists
        const deleteId = pendingDeleteIdRef.current;
        pendingDeleteIdRef.current = null;
        dispatch(
          appActions.showModal({
            title: "Cannot Delete Feature",
            subTitle: "The deletion may not have completed. Please try again.",
            showCloseButton: false,
            buttons: [
              {
                text: "OK",
                variant: "filled",
                onClick: () => dispatch(appActions.hideModal()),
              },
            ],
          })
        );
      }
    }
  }, [features, dispatch]);

  const tableHeaders = useMemo(
    () => [
      { id: 1, title: "No", dataKey: "no" },
      { id: 2, title: "Name", dataKey: "name" },
      { id: 3, title: "Name (Khmer)", dataKey: "name_kh" },
      { id: 4, title: "Name (Chinese)", dataKey: "name_zh" },
    ],
    []
  );

  // Add sequential 'no' field for display
  const featuresWithNo = (features ?? []).map((item, idx) => ({
    ...item,
    no: idx + 1,
  }));

  const handleRowDelete = useCallback(
    (data: Feature) => {
      dispatch(
        appActions.showModal({
          title: "Delete Confirmation",
          subTitle: `Are you sure you want to delete "${data.name}"?`,
          buttons: [
            {
              text: "Cancel",
              variant: "border",
              onClick: () => dispatch(appActions.hideModal()),
            },
            {
              text: "Delete",
              variant: "filled",
              onClick: () => {
                dispatch(appActions.hideModal());
                dispatch(appActions.showLoading());
                dispatch(
                  featureActions.deleteFeature.started({
                    id: data.id,
                    onComplete: () => {
                      dispatch(appActions.hideLoading());
                    },
                    onSuccess: () => {
                      // Refetch features on successful deletion
                      dispatch(
                        featureActions.fetchFeatures.started({})
                      );
                      // Show success message
                      // dispatch(
                      //   appActions.showModal({
                      //     title: "Success",
                      //     subTitle: `Feature "${data.name}" has been deleted successfully.`,
                      //     buttons: [
                      //       {
                      //         text: "OK",
                      //         variant: "filled",
                      //         onClick: () => dispatch(appActions.hideModal()),
                      //       },
                      //     ],
                      //   })
                      // );
                    },
                    onError: (error: any) => {
                      // Check if this is a timeout/network error (no response status)
                      // If deletion times out, refetch to verify if it actually succeeded
                      const isTimeout = !error.response?.status && (
                        error.code === 'ECONNABORTED' ||
                        error.message?.includes('timeout') ||
                        error.message?.includes('Network Error')
                      );

                      if (isTimeout) {
                        // Store the ID we're trying to delete
                        pendingDeleteIdRef.current = data.id;
                        // Refetch features to check if deletion actually succeeded
                        dispatch(featureActions.fetchFeatures.started({}));
                        // Don't show error yet - wait for refetch to complete
                        return;
                      }

                      // For other errors, show the error message
                      const errorMessage = parseApiError(error);
                      dispatch(
                        appActions.showModal({
                          title: "Cannot Delete Feature",
                          subTitle: errorMessage,
                          showCloseButton: false,
                          buttons: [
                            {
                              text: "OK",
                              variant: "filled",
                              onClick: () => dispatch(appActions.hideModal()),
                            },
                          ],
                        })
                      );
                    },
                  })
                );
              },
            },
          ],
        })
      );
    },
    [dispatch]
  );

  // handle modal
  const { isOpen: isCreateModalOpen, openModal: handleOpenCreateModal, closeModal: handleCloseCreateModal } = useModalWithProtection();
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const handleOpenEditModal = () => setEditModalOpen(true);
  const handleCloseEditModal = () => setEditModalOpen(false);

  return {
    tableHeaders,
    features: featuresWithNo,
    refetchFeatures,
    handleRowDelete,
    isCreateModalOpen,
    isEditModalOpen,
    handleOpenCreateModal,
    handleCloseCreateModal,
    handleOpenEditModal,
    handleCloseEditModal
  };
};

export const useFeatureToast = () => {
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
    visible: boolean;
  }>({ message: "", type: "success", visible: false });

  const showToast = (message: string, type: ToastType = "success") => {
    setToast({ message, type, visible: true });
    setTimeout(() => {
      setToast((t) => ({ ...t, visible: false }));
    }, 2000);
  };

  const hideToast = () => setToast((t) => ({ ...t, visible: false }));

  return { toast, showToast, hideToast };
};

export const useFeatureRegistration = (
  showToast: (msg: string, type?: ToastType) => void,
  refetchFeatures?: () => void
) => {
  const dispatch = useDispatch<AppDispatch>();

  const handleRegisterFeature = useCallback(
    (
      data: FeatureCreateRequestBody,
      onError: (message: string) => void,
      onSuccess?: (result: any) => void
    ) => {
      dispatch(
        featureActions.createNewFeature.started({
          ...data,
          onComplete: () => { },
          onSuccess: (result: any) => {
            showToast("Feature created successfully!", "success");
            setTimeout(() => {
              if (refetchFeatures) refetchFeatures();
              if (onSuccess) onSuccess(result);
            }, 1200);
          },
          onError: (_err: any) => {
            showToast("Failed to create feature. Please try again.", "error");
            if (onError) onError("Failed to create feature. Please try again.");
          },
        })
      );
    },
    [dispatch, showToast, refetchFeatures]
  );

  return { handleRegisterFeature };
};

export const useFeatureEditing = (
  showToast: (msg: string, type?: ToastType) => void,
  refetchFeatures?: () => void,
  closeModal?: () => void
) => {
  const dispatch = useDispatch<AppDispatch>();

  const handleEditFeature = useCallback(
    (
      data: FeatureEditRequestBody,
      id: string,
      onError: (message: string) => void,
      onSuccess?: (result: any) => void
    ) => {
      return dispatch(
        featureActions.updateFeature.started({
          ...data,
          id,
          onComplete: () => { },
          onSuccess: (result: any) => {
            showToast("Feature updated succesfully!", "success");
            setTimeout(() => {
              if (refetchFeatures) refetchFeatures();
              if (closeModal) closeModal();
              if (onSuccess) onSuccess(result);
            }, 1200);
          },
          onError: (_err: any) => {
            showToast("Failed to update feature. Please try again.", "error");
            if (onError) onError("Failed to update feature. Please try again.");
          },
        })
      );
    }, [dispatch, showToast, refetchFeatures, closeModal]
  );
  return { handleEditFeature }
};
